import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Icon from '../ui/Icon';
import { Toggle } from '../ui/primitives';
import { useAdminToast } from './AdminShell';
import { API_URL } from '../config/api';

/* ============================================================================
   Edytor etykiet — w pełni wizualny drag & drop.
   - Bloki przeciągasz, skalujesz uchwytami, ustawiasz właściwości.
   - Wymiar etykiety (mm) jest pierwszorzędnym polem: presety + własny rozmiar.
   - Podgląd jest CLIENT-SIDE (natychmiastowy, bez zależności od backendu) —
     dlatego nie pojawia się już "Błąd generowania podglądu".
   - Model bloków jest osadzany w zapisanym Liquid jako komentarz PACKFLOW_EDITOR_V2,
     więc szablon nadal w pełni przechodzi backendowy pipeline (liquidjs + PDF).
   ========================================================================== */

const PX_PER_MM = 3.2;                 // skala wyświetlania kanwy
const mmToPx = (mm: number) => Math.round(mm * PX_PER_MM);

type Mm = { w: number; h: number };

const FORMATS: { id: string; name: string; w: number; h: number }[] = [
  { id: 'a6', name: 'A6 — 105 × 148 mm', w: 105, h: 148 },
  { id: 'a5', name: 'A5 — 148 × 210 mm', w: 148, h: 210 },
  { id: 'a4', name: 'A4 — 210 × 297 mm', w: 210, h: 297 },
  { id: '80x50', name: 'Etykieta 80 × 50 mm', w: 80, h: 50 },
  { id: '100x150', name: 'Etykieta 100 × 150 mm', w: 100, h: 150 },
  { id: '62x29', name: 'Etykieta 62 × 29 mm', w: 62, h: 29 },
  { id: '57x32', name: 'Etykieta 57 × 32 mm', w: 57, h: 32 },
];

// Domyślny wymiar fizyczny per szablon (gdy zapis nie zawiera pageMm).
const TEMPLATE_DEFAULT_MM: Record<string, Mm> = {
  shipping_label_a6: { w: 105, h: 148 },
  shipping_label: { w: 105, h: 148 },
  product_labels: { w: 80, h: 50 },
  goodspeed_label: { w: 105, h: 148 },
  logo_label: { w: 148, h: 105 },
  packing_list: { w: 210, h: 297 },
  shipping_label_80mm: { w: 80, h: 120 },
};
const DEFAULT_MM: Mm = { w: 105, h: 148 };

/* ---- przykładowe dane do podglądu ---- */
const SAMPLE: Record<string, string> = {
  order_name: '#1042', 'order.name': '#1042', 'order.total': '84,00 zł',
  'customer.first_name': 'Anna', 'customer.last_name': 'Kowalska',
  'customer.name': 'Anna Kowalska', 'customer.phone': '+48 600 100 200',
  'shipping_address.first_name': 'Anna', 'shipping_address.last_name': 'Kowalska',
  'shipping_address.address1': 'ul. Floriańska 12/4', 'shipping_address.address2': '',
  'shipping_address.city': 'Kraków', 'shipping_address.zip': '30-001',
  'shipping_address.phone': '+48 600 100 200',
  'shipping_line.code': 'Kurier DHL', 'line_item.product.title': 'Chleb Żytni na Zakwasie',
  // Wariant sztuki (np. posypka) — pusty dla produktu bez opcji.
  'line_item.variant_title': 'Sezam biały',
  'shipment.tracking': '6280 0192 5715', note: 'Proszę zostawić pod drzwiami',
  'package.weight': '0,5 kg', 'package.dims': '30 × 25 × 15 cm',
  shop_name: 'Chlebowa Paczka', 'shop.address': 'ul. Przykładowa 1, 00-001 Warszawa',
  'order.created_at': '11.06.2026',
};
const SAMPLE_ITEMS = [
  { title: 'Chleb żytni na zakwasie 700g', qty: 2 },
  { title: 'Bułka grahamka', qty: 4 },
  { title: 'Croissant maślany', qty: 3 },
];
const VAR_CHIPS = [
  'order_name', 'customer.first_name', 'shipping_address.first_name', 'shipping_address.last_name',
  'shipping_address.address1', 'shipping_address.city', 'shipping_address.zip',
  'shipping_address.phone', 'shipping_line.code', 'shipment.tracking', 'line_item.product.title',
  'line_item.variant_title', 'note',
  'shop_name',
];

// Bardzo lekka interpolacja Liquid TYLKO na potrzeby podglądu (nie zastępuje backendu).
function resolveExpr(expr: string): string {
  const parts = expr.split('|').map((p) => p.trim());
  let val: string | undefined = SAMPLE[parts[0]];
  for (let i = 1; i < parts.length; i++) {
    const m = parts[i].match(/^default:\s*['"]?(.*?)['"]?$/);
    if (m && (val === undefined || val === null || val === '')) val = m[1];
  }
  if (val === undefined || val === null) return `⟨${parts[0]}⟩`;
  return val;
}
function interpolate(s: string): string {
  if (!s) return '';
  return s
    .replace(/\{\{(.*?)\}\}/g, (_m, expr) => resolveExpr(String(expr).trim()))
    // Znaczniki sterujące Liquid ({% if %}, {% for %}…) to logika, nie tekst — w podglądzie
    // ukrywamy je, żeby nie „wyciekały" jako litery na etykiecie (render po stronie backendu).
    .replace(/\{%-?[\s\S]*?-?%\}/g, '');
}
const esc = (s: string) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/* ---- model bloku ---- */
const BLOCK_DEFS = [
  { type: 'text', label: 'Tekst', icon: 'type' },
  { type: 'variable', label: 'Pole danych', icon: 'braces' },
  { type: 'image', label: 'Obraz / logo', icon: 'image' },
  { type: 'table', label: 'Tabela pozycji', icon: 'table' },
  { type: 'barcode', label: 'Kod kreskowy', icon: 'barcode' },
  { type: 'qr', label: 'Kod QR', icon: 'qr-code' },
  { type: 'box', label: 'Prostokąt', icon: 'square' },
  { type: 'line', label: 'Linia', icon: 'minus' },
] as const;
type BlockType = typeof BLOCK_DEFS[number]['type'];

type El = {
  id: number; type: BlockType; x: number; y: number; w?: number; h?: number;
  text?: string; variable?: string; src?: string;
  fontSize?: number; bold?: boolean; italic?: boolean; align?: 'left' | 'center' | 'right';
  color?: string; bg?: string; borderColor?: string; radius?: number;
  // Ramka: przerywana linia i nagłówek (np. „Uwagi" na etykiecie GoodSpeed) — jak serwerowy `box`.
  dashed?: boolean; label?: string;
  // Tekst / pole danych: „Dopasuj rozmiar do pola" — stałe pole w×h, czcionka maleje od fontSize.
  autoFit?: boolean;
};

let UID = 100;
function defaultEl(type: BlockType, x: number, y: number): El {
  const base: El = { id: ++UID, type, x, y };
  switch (type) {
    case 'text': return { ...base, text: 'Nowy tekst', fontSize: 14, bold: false, align: 'left', color: '#111111', w: 180 };
    case 'variable': return { ...base, variable: 'shipment.tracking', fontSize: 14, bold: true, align: 'left', color: '#111111', w: 180 };
    case 'image': return { ...base, w: 110, h: 50, src: '' };
    case 'table': return { ...base, w: 240, h: 96, fontSize: 11, color: '#111111' };
    case 'barcode': return { ...base, w: 200, h: 60, variable: 'shipment.tracking' };
    case 'qr': return { ...base, w: 84, h: 84, variable: 'order_name' };
    case 'box': return { ...base, w: 160, h: 70, bg: '#f1f5f9', borderColor: '#111111', radius: 6 };
    case 'line': return { ...base, w: 200, h: 2, bg: '#111111' };
    default: return base;
  }
}

/* ---- domyślne układy wbudowanych szablonów ----
   Dzięki nim KAŻDA wbudowana etykieta otwiera się w edytorze wizualnym (drag & drop),
   a nie w trybie „Kod Liquid". Stosowane, gdy zapis nie zawiera modelu PACKFLOW_EDITOR_V2
   (świeży seed/legacy) oraz przez przycisk „Przywróć domyślny”. Współrzędne w px (3.2 px/mm). */
type DefEl = Omit<El, 'id'>;
// Zachowanie strony zapisywane w modelu PACKFLOW_EDITOR_V2 (jak serwerowy editorModel.ts):
// repeat — jedna kopia układu na każdą zamówioną sztukę (pętla po line_items),
// rotate — treść obrócona o 90° (poziomy układ na pionowej stronie).
type PageOpts = { repeat?: 'per_unit'; rotate?: 90 };
const C = '#111111';
const BUILTIN_DEFAULTS: Record<string, { pageMm: Mm; els: DefEl[]; repeat?: 'per_unit' }> = {
  product_labels: {
    pageMm: { w: 80, h: 50 },
    // Etykieta na każdą sztukę — tylko w tej pętli istnieje `line_item` (nazwa produktu).
    repeat: 'per_unit',
    els: [
      { type: 'text', x: 28, y: 26, w: 200, text: '{{ customer.first_name }}, oto', fontSize: 14, align: 'center', color: C },
      { type: 'text', x: 16, y: 54, w: 224, text: '{{ line_item.product.title }}', fontSize: 18, bold: true, align: 'center', color: C },
      { type: 'text', x: 28, y: 96, w: 200, text: 'Życzymy smacznego :)', fontSize: 13, align: 'center', color: C },
      { type: 'text', x: 16, y: 126, w: 224, text: 'Numer zamówienia: {{ order_name }}', fontSize: 13, bold: true, align: 'center', color: C },
    ],
  },
  shipping_label: {
    pageMm: { w: 105, h: 148 },
    els: [
      { type: 'text', x: 24, y: 22, w: 288, text: 'NADAWCA', fontSize: 12, bold: true, align: 'left', color: '#555' },
      { type: 'text', x: 24, y: 44, w: 288, text: '{{ shop_name }}\n{{ shop.address }}', fontSize: 14, align: 'left', color: C },
      { type: 'line', x: 24, y: 112, w: 288, h: 2, bg: C },
      { type: 'text', x: 24, y: 128, w: 288, text: 'ODBIORCA', fontSize: 12, bold: true, align: 'left', color: '#555' },
      { type: 'text', x: 24, y: 150, w: 288, text: '{{ shipping_address.first_name }} {{ shipping_address.last_name }}', fontSize: 18, bold: true, align: 'left', color: C },
      { type: 'text', x: 24, y: 184, w: 288, text: '{{ shipping_address.address1 }}', fontSize: 15, align: 'left', color: C },
      { type: 'text', x: 24, y: 210, w: 288, text: '{{ shipping_address.zip }} {{ shipping_address.city }}', fontSize: 15, align: 'left', color: C },
      { type: 'text', x: 24, y: 238, w: 288, text: 'tel. {{ shipping_address.phone }}', fontSize: 13, align: 'left', color: C },
      { type: 'barcode', x: 24, y: 384, w: 288, h: 70, variable: 'shipment.tracking' },
    ],
  },
  packing_list: {
    pageMm: { w: 210, h: 297 },
    els: [
      { type: 'text', x: 32, y: 28, w: 440, text: '{{ shop_name }}', fontSize: 24, bold: true, align: 'left', color: C },
      { type: 'text', x: 32, y: 70, w: 440, text: 'LIST PAKOWANIA', fontSize: 20, bold: true, align: 'left', color: '#555' },
      { type: 'text', x: 32, y: 116, w: 440, text: 'Zamówienie: {{ order_name }}', fontSize: 15, align: 'left', color: C },
      { type: 'text', x: 32, y: 142, w: 440, text: 'Data: {{ order.created_at }}', fontSize: 14, align: 'left', color: C },
      { type: 'text', x: 32, y: 166, w: 580, text: 'Klient: {{ shipping_address.first_name }} {{ shipping_address.last_name }}', fontSize: 14, align: 'left', color: C },
      { type: 'text', x: 32, y: 190, w: 600, text: 'Adres: {{ shipping_address.address1 }}, {{ shipping_address.zip }} {{ shipping_address.city }}', fontSize: 13, align: 'left', color: C },
      { type: 'line', x: 32, y: 224, w: 608, h: 2, bg: C },
      { type: 'table', x: 32, y: 244, w: 608, h: 360, fontSize: 13, color: C },
      { type: 'text', x: 32, y: 880, w: 440, text: 'Dziękujemy za zakupy! :)', fontSize: 14, align: 'left', color: C },
    ],
  },
  goodspeed_label: {
    pageMm: { w: 148, h: 105 },
    els: [
      { type: 'box', x: 16, y: 16, w: 442, h: 54, bg: 'transparent', borderColor: C, radius: 4 },
      { type: 'text', x: 28, y: 30, w: 418, text: 'Zamówienie: {{ order_name }}', fontSize: 22, bold: true, align: 'left', color: C },
      { type: 'box', x: 16, y: 84, w: 250, h: 236, bg: 'transparent', borderColor: C, radius: 4 },
      { type: 'text', x: 28, y: 96, w: 226, text: 'Imię: {{ customer.first_name }}', fontSize: 14, align: 'left', color: C },
      { type: 'text', x: 28, y: 126, w: 226, text: 'Telefon: {{ shipping_address.phone }}', fontSize: 14, align: 'left', color: C },
      { type: 'text', x: 28, y: 156, w: 226, text: 'Adres: {{ shipping_address.address1 }}', fontSize: 14, align: 'left', color: C },
      { type: 'text', x: 28, y: 206, w: 226, text: '{{ shipping_address.zip }} {{ shipping_address.city }}', fontSize: 14, align: 'left', color: C },
      { type: 'text', x: 28, y: 238, w: 226, text: 'Region: {{ shipping_line.code }}', fontSize: 14, align: 'left', color: C },
      { type: 'box', x: 278, y: 84, w: 180, h: 236, bg: 'transparent', borderColor: C, radius: 4 },
      { type: 'text', x: 290, y: 96, w: 156, text: 'UWAGI', fontSize: 12, bold: true, align: 'left', color: '#555' },
      { type: 'text', x: 290, y: 122, w: 156, text: '{{ note }}', fontSize: 13, align: 'left', color: C },
    ],
  },
  logo_label: {
    pageMm: { w: 148, h: 105 },
    els: [
      { type: 'image', x: 87, y: 93, w: 300, h: 150, src: '' },
    ],
  },
};

function cloneEls(list: DefEl[]): El[] {
  return list.map((e) => ({ ...e, id: ++UID }));
}

/* ---- „Dopasuj rozmiar do pola" ----
   Blok tekstu z autoFit to stałe pole w×h: czcionka zaczyna od fontSize (maksimum) i maleje, aż treść
   zmieści się w polu. Liczy przeglądarka na prawdziwych fontach — w wydruku (puppeteer) i w podglądzie
   robi to FIT_SCRIPT dołączany do szablonu, na kanwie edytora ten sam algorytm (fitToField). */
const FIT_MIN_FONT_PX = 6;
// Kopia 1:1 FIT_SCRIPT z api/src/modules/printing/editorModel.ts (tam testy) — zmieniaj obie naraz.
// Nie może zawierać ogranicznika Liquid (podwójna klamra / klamra z procentem) — jest częścią szablonu.
const FIT_SCRIPT =
  '(function(){' +
  `var MIN=${FIT_MIN_FONT_PX};` +
  'function fits(el){return el.scrollHeight<=el.clientHeight&&el.scrollWidth<=el.clientWidth;}' +
  'function largest(el,lo,hi){el.style.fontSize=hi+"px";if(fits(el))return hi;' +
  'for(var i=0;i<14;i++){var mid=(lo+hi)/2;el.style.fontSize=mid+"px";if(fits(el))lo=mid;else hi=mid;}return lo;}' +
  'function fit(el){var max=parseFloat(el.getAttribute("data-pf-fit"))||12;var min=Math.min(MIN,max);' +
  'el.style.overflowWrap="normal";var size=largest(el,min,max);el.style.fontSize=size+"px";' +
  'if(!fits(el)){el.style.overflowWrap="anywhere";size=largest(el,min,max);}' +
  'el.style.fontSize=Math.floor(size*4)/4+"px";}' +
  'function run(){var list=document.querySelectorAll("[data-pf-fit]");for(var i=0;i<list.length;i++)fit(list[i]);}' +
  'run();if(document.fonts&&document.fonts.ready)document.fonts.ready.then(run);' +
  '})();';

function isAutoFit(el: El): boolean {
  return !!el.autoFit && (el.type === 'text' || el.type === 'variable');
}
// Wysokość pola; zapis bez niej (ręcznie edytowany model) dostaje dwie linie — jak na serwerze.
function fitHeight(el: El, fontSize: number): number {
  return el.h ?? Math.round(fontSize * 1.3 * 2);
}
// Atrybut + styl pola w eksporcie/podglądzie; puste dla zwykłego, płynącego tekstu.
function fitField(el: El, fontSize: number): { attr: string; css: string } {
  if (!isAutoFit(el)) return { attr: '', css: '' };
  return { attr: ` data-pf-fit="${fontSize}"`, css: `height:${fitHeight(el, fontSize)}px;overflow:hidden;` };
}

// Algorytm FIT_SCRIPT dla kanwy edytora (żywy podgląd przy pisaniu i przeciąganiu uchwytu).
function fitToField(node: HTMLElement, maxPx: number): void {
  const fits = () => node.scrollHeight <= node.clientHeight && node.scrollWidth <= node.clientWidth;
  const min = Math.min(FIT_MIN_FONT_PX, maxPx);
  const largest = (): number => {
    let lo = min;
    let hi = maxPx;
    node.style.fontSize = `${hi}px`;
    if (fits()) return hi;
    for (let i = 0; i < 14; i++) {
      const mid = (lo + hi) / 2;
      node.style.fontSize = `${mid}px`;
      if (fits()) lo = mid; else hi = mid;
    }
    return lo;
  };
  node.style.overflowWrap = 'normal';
  let size = largest();
  node.style.fontSize = `${size}px`;
  if (!fits()) { node.style.overflowWrap = 'anywhere'; size = largest(); }
  node.style.fontSize = `${Math.floor(size * 4) / 4}px`;
}

// Pole przy włączaniu: wnętrze najmniejszej ramki, w której leży blok (np. „Uwagi" na etykiecie
// GoodSpeed — z takim samym odstępem od krawędzi jak z lewej/góry), a bez ramki dwie linie tekstu.
function defaultFitField(el: El, all: El[]): { w?: number; h: number } {
  const lines = (n: number) => Math.round((el.fontSize || 14) * 1.3 * n);
  const box = all
    .filter((b) => b.type === 'box' && b.w != null && b.h != null
      && el.x >= b.x && el.y >= b.y && el.x < b.x + (b.w || 0) && el.y < b.y + (b.h || 0))
    .sort((a, b) => (a.w || 0) * (a.h || 0) - (b.w || 0) * (b.h || 0))[0];
  if (!box || box.w == null || box.h == null) return { h: lines(2) };
  const right = box.x + box.w - Math.min(el.x - box.x, 16);
  const bottom = box.y + box.h - Math.min(el.y - box.y, 16);
  return {
    w: Math.max(16, Math.min(el.w ?? box.w, right - el.x)),
    h: Math.max(lines(1), bottom - el.y),
  };
}

function FitText({ style, height, maxPx, children }: { style: React.CSSProperties; height: number; maxPx: number; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Bez listy zależności: każda zmiana treści, wymiaru czy stylu przelicza pole przed malowaniem.
  useLayoutEffect(() => { if (ref.current) fitToField(ref.current, maxPx); });
  return (
    <div ref={ref} style={{ ...style, height, overflow: 'hidden', outline: '1px dashed rgba(59,130,246,.6)' }}>
      {children}
    </div>
  );
}

/* ---- render bloku na kanwie (z przykładowymi danymi) ---- */
function ElementView({ el }: { el: El }) {
  const align = el.align || 'left';
  if (el.type === 'text' || el.type === 'variable') {
    const content = el.type === 'variable' ? resolveExpr(el.variable || '') : interpolate(el.text || '');
    const style: React.CSSProperties = { fontSize: el.fontSize, fontWeight: el.bold ? 700 : 400, fontStyle: el.italic ? 'italic' : 'normal', color: el.color || '#111', lineHeight: 1.3, fontFamily: 'Arial, sans-serif', width: el.w, textAlign: align, whiteSpace: 'pre-line' };
    if (isAutoFit(el)) {
      const maxPx = el.fontSize || 14;
      return <FitText style={style} height={fitHeight(el, maxPx)} maxPx={maxPx}>{content}</FitText>;
    }
    return <div style={style}>{content}</div>;
  }
  if (el.type === 'image') {
    return el.src
      ? <img src={el.src} alt="" style={{ width: el.w, height: el.h, objectFit: 'contain', display: 'block' }} />
      : <div style={{ width: el.w, height: el.h, background: '#eef1f5', border: '1px dashed #9aa6b2', display: 'grid', placeItems: 'center', color: '#7a8794', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>LOGO / OBRAZ</div>;
  }
  if (el.type === 'box') {
    return (
      <div style={{ width: el.w, height: el.h, background: el.bg || 'transparent', border: `1.5px ${el.dashed ? 'dashed' : 'solid'} ${el.borderColor || '#111'}`, borderRadius: el.radius || 0 }}>
        {el.label && <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '2px 4px', fontFamily: 'Arial, sans-serif', color: '#111' }}>{el.label}</div>}
      </div>
    );
  }
  if (el.type === 'line') {
    return <div style={{ width: el.w, height: el.h || 2, background: el.bg || '#111' }} />;
  }
  if (el.type === 'table') {
    return (
      <div style={{ width: el.w, fontSize: el.fontSize || 11, color: el.color || '#111', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1.5px solid currentColor', fontWeight: 700, padding: '2px 0' }}><span>Produkt</span><span>Ilość</span></div>
        {SAMPLE_ITEMS.map((it, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #ddd', padding: '2px 0' }}><span>{it.title}</span><span>{it.qty}</span></div>
        ))}
      </div>
    );
  }
  if (el.type === 'barcode') {
    const w = el.w || 200; const h = el.h || 60;
    return (
      <div style={{ width: w }}>
        <svg width={w} height={h - 14} viewBox={`0 0 ${w} ${h - 14}`} preserveAspectRatio="none">
          {Array.from({ length: 52 }).map((_, i) => (
            <rect key={i} x={i * (w / 52)} y="0" width={(w / 52) * (i % 2 ? 0.65 : 0.3)} height={h - 14} fill="#111" />
          ))}
        </svg>
        <div style={{ fontSize: 10, fontFamily: 'monospace', textAlign: 'center', color: '#111' }}>{resolveExpr(el.variable || '')}</div>
      </div>
    );
  }
  if (el.type === 'qr') {
    const s = Math.min(el.w || 84, el.h || 84);
    return (
      <svg width={s} height={s} viewBox="0 0 10 10">
        {Array.from({ length: 100 }).map((_, i) => (((i * 7 + ((i / 10) | 0) * 3) % 5 < 2 || i % 11 === 0)
          ? <rect key={i} x={i % 10} y={(i / 10) | 0} width="1" height="1" fill="#111" /> : null))}
      </svg>
    );
  }
  return null;
}

/* ---- Liquid round-trip ---- */
const MARKER = 'PACKFLOW_EDITOR_V2';

function styleFor(el: El, withPos = true): string {
  const pos = withPos ? `position:absolute;left:${el.x}px;top:${el.y}px;` : '';
  return pos;
}

// Ramka jak serwerowy `box` (editorModel.ts): przerywana linia i nagłówek. Bez tego zapis z edytora
// gubił nagłówek „Uwagi" i przerywaną ramkę etykiety GoodSpeed.
function boxHtml(el: El, base: string): string {
  const label = el.label
    ? `<div style="font-size:11px;font-weight:800;text-transform:uppercase;padding:2px 4px;">${esc(el.label)}</div>`
    : '';
  return `<div style="${base}width:${el.w}px;height:${el.h}px;background:${el.bg || 'transparent'};border:1.5px ${el.dashed ? 'dashed' : 'solid'} ${el.borderColor || '#111'};border-radius:${el.radius || 0}px;box-sizing:border-box;">${label}</div>`;
}

// HTML zapisywany do szablonu (zawiera znaczniki Liquid — render po stronie backendu).
function elToHtml(el: El): string {
  const align = el.align || 'left';
  const base = styleFor(el);
  switch (el.type) {
    case 'text': {
      const fit = fitField(el, el.fontSize || 14);
      return `<div${fit.attr} style="${base}width:${el.w}px;${fit.css}font-size:${el.fontSize}px;font-weight:${el.bold ? 700 : 400};font-style:${el.italic ? 'italic' : 'normal'};color:${el.color || '#111'};text-align:${align};line-height:1.3;white-space:pre-line;">${el.text || ''}</div>`;
    }
    case 'variable': {
      const fit = fitField(el, el.fontSize || 14);
      return `<div${fit.attr} style="${base}width:${el.w}px;${fit.css}font-size:${el.fontSize}px;font-weight:${el.bold ? 700 : 500};font-style:${el.italic ? 'italic' : 'normal'};color:${el.color || '#111'};text-align:${align};">{{ ${el.variable} }}</div>`;
    }
    case 'image': {
      const img = (src: string) => `<img src="${src}" style="${base}width:${el.w}px;height:${el.h}px;object-fit:contain;" />`;
      // Bez własnego obrazu blok „Obraz / logo" drukuje logo sklepu (jak blok `logo` na serwerze),
      // ale tylko gdy jest ustawione — puste src Chromium drukuje jako ikonę uszkodzonego obrazka.
      return el.src ? img(el.src) : `{% if shop_logo_url != blank %}${img('{{ shop_logo_url }}')}{% endif %}`;
    }
    case 'box':
      return boxHtml(el, base);
    case 'line':
      return `<div style="${base}width:${el.w}px;height:${el.h || 2}px;background:${el.bg || '#111'};"></div>`;
    case 'table':
      return `<table style="${base}width:${el.w}px;border-collapse:collapse;font-size:${el.fontSize || 11}px;color:${el.color || '#111'};">
<thead><tr><th style="text-align:left;border-bottom:1.5px solid currentColor;">Produkt</th><th style="text-align:right;border-bottom:1.5px solid currentColor;">Ilość</th></tr></thead>
<tbody>{% for item in line_items %}<tr><td style="border-bottom:1px solid #ddd;">{{ item.product.title }}</td><td style="text-align:right;border-bottom:1px solid #ddd;">{{ item.quantity }}</td></tr>{% endfor %}</tbody>
</table>`;
    case 'barcode':
      return `<img src="https://barcodeapi.org/api/128/{{ ${el.variable} | url_encode }}" style="${base}width:${el.w}px;height:${el.h}px;" />`;
    case 'qr':
      return `<img src="https://api.qrserver.com/v1/create-qr-code/?size=${el.w}x${el.h}&data={{ ${el.variable} | url_encode }}" style="${base}width:${el.w}px;height:${el.h}px;" />`;
    default:
      return '';
  }
}

// HTML do podglądu w przeglądarce (interpolacja przykładowych danych, realne kody).
function elToPreviewHtml(el: El): string {
  const align = el.align || 'left';
  const base = styleFor(el);
  switch (el.type) {
    case 'text':
    case 'variable': {
      const content = el.type === 'variable' ? resolveExpr(el.variable || '') : interpolate(el.text || '');
      const fit = fitField(el, el.fontSize || 14);
      return `<div${fit.attr} style="${base}width:${el.w}px;${fit.css}font-size:${el.fontSize}px;font-weight:${el.bold ? 700 : 400};font-style:${el.italic ? 'italic' : 'normal'};color:${el.color || '#111'};text-align:${align};line-height:1.3;white-space:pre-line;">${esc(content)}</div>`;
    }
    case 'image':
      return el.src
        ? `<img src="${el.src}" style="${base}width:${el.w}px;height:${el.h}px;object-fit:contain;" />`
        : `<div style="${base}width:${el.w}px;height:${el.h}px;background:#eef1f5;border:1px dashed #9aa6b2;"></div>`;
    case 'box':
      return boxHtml(el, base);
    case 'line':
      return `<div style="${base}width:${el.w}px;height:${el.h || 2}px;background:${el.bg || '#111'};"></div>`;
    case 'table': {
      const rows = SAMPLE_ITEMS.map((it) => `<tr><td style="border-bottom:1px solid #ddd;">${esc(it.title)}</td><td style="text-align:right;border-bottom:1px solid #ddd;">${it.qty}</td></tr>`).join('');
      return `<table style="${base}width:${el.w}px;border-collapse:collapse;font-size:${el.fontSize || 11}px;color:${el.color || '#111'};"><thead><tr><th style="text-align:left;border-bottom:1.5px solid currentColor;">Produkt</th><th style="text-align:right;border-bottom:1.5px solid currentColor;">Ilość</th></tr></thead><tbody>${rows}</tbody></table>`;
    }
    case 'barcode':
      return `<img src="https://barcodeapi.org/api/128/${encodeURIComponent(resolveExpr(el.variable || ''))}" style="${base}width:${el.w}px;height:${el.h}px;" onerror="this.style.background='#111'" />`;
    case 'qr':
      return `<img src="https://api.qrserver.com/v1/create-qr-code/?size=${el.w}x${el.h}&data=${encodeURIComponent(resolveExpr(el.variable || ''))}" style="${base}width:${el.w}px;height:${el.h}px;" />`;
    default:
      return '';
  }
}

// px CSS, w których puppeteer renderuje PDF (96 dpi) — kanwa edytora ma własną skalę (PX_PER_MM).
const CSS_PX_PER_MM = 96 / 25.4;

// Opakowanie strony 1:1 jak kanoniczny eksporter serwera (api printing/editorModel.ts): zoom kanwy
// do fizycznej strony, reset marginesu body, obrót 90° i pętla „etykieta na sztukę". Bez tego zapis
// z edytora drukował jedną etykietę bez nazwy produktu, w ~85% rozmiaru i przesuniętą o margines.
function exportLiquid(els: El[], sizePx: { w: number; h: number }, pageMm: Mm, opts: PageOpts = {}): string {
  const model = JSON.stringify({ size: sizePx, pageMm, els, ...opts });
  const body = els.map(elToHtml).filter(Boolean).join('\n');

  // Dryf <1% pomijamy, żeby nigdy nie emitować zoomu ~1.00x.
  let scale = 1;
  const zoom = Math.round(((pageMm.w * CSS_PX_PER_MM) / sizePx.w) * 1000) / 1000;
  if (Math.abs(zoom - 1) > 0.01) scale = zoom;
  const zoomStyle = scale !== 1 ? `zoom:${scale};` : '';

  let canvas = `<div style="position:relative;width:${sizePx.w}px;height:${sizePx.h}px;font-family:Arial,sans-serif;background:#fff;overflow:hidden;${zoomStyle}">\n${body}\n</div>`;

  if (opts.rotate === 90) {
    // Wrapper i translateY z wymiarów PO skalowaniu — inaczej obrót ucina kanwę (fix przepełnienia A6).
    const sw = Math.round(sizePx.w * scale * 1000) / 1000;
    const sh = Math.round(sizePx.h * scale * 1000) / 1000;
    canvas = `<div style="width:${sh}px;height:${sw}px;overflow:hidden;"><div style="transform:rotate(90deg) translateY(-${sh}px);transform-origin:top left;">${canvas}</div></div>`;
  }

  // Domyślny margines body (8px) wypychał kanwę poza stronę i przesuwał wydruk.
  const styleRules = ['html,body{margin:0;padding:0;}'];

  if (opts.repeat === 'per_unit') {
    // :last-child zdejmuje łamanie po ostatniej kopii — bez pustej „białej etykiety" na końcu.
    styleRules.push('.pf-unit{page-break-after:always;}', '.pf-unit:last-child{page-break-after:auto;}');
    canvas = `{% for line_item in line_items %}{% for i in (1..line_item.quantity) %}<div class="pf-unit">${canvas}</div>{% endfor %}{% endfor %}`;
  }

  // Po kanwie (i poza pętlą „na sztukę") — jedno uruchomienie dopasowuje pola wszystkich kopii.
  const script = els.some(isAutoFit) ? `<script>${FIT_SCRIPT}</script>\n` : '';
  return `<!--${MARKER} ${model}-->\n<style>${styleRules.join('')}</style>\n${canvas}\n${script}`;
}

function importLiquid(content: string): ({ els: El[]; size?: { w: number; h: number }; pageMm?: Mm } & PageOpts) | null {
  const m = content.match(new RegExp(`<!--${MARKER} (.*?)-->`, 's'));
  if (!m) return null;
  try {
    const parsed = JSON.parse(m[1]);
    let els: El[] = (parsed.els || []).map((e: any) => (e.type === 'logo' ? { ...e, type: 'image' } : e));
    UID = Math.max(UID, ...els.map((e) => e.id || 0), 100);
    // repeat/rotate z modelu (np. z seedu) muszą przetrwać ponowny zapis z edytora.
    return {
      els, size: parsed.size, pageMm: parsed.pageMm,
      repeat: parsed.repeat === 'per_unit' ? 'per_unit' : undefined,
      rotate: parsed.rotate === 90 ? 90 : undefined,
    };
  } catch { return null; }
}

function previewDoc(els: El[], sizePx: { w: number; h: number }, pageMm: Mm): string {
  const body = els.map(elToPreviewHtml).filter(Boolean).join('\n');
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
    @page { size: ${pageMm.w}mm ${pageMm.h}mm; margin: 0; }
    html,body{margin:0;padding:0;background:#e9edf3;}
    .wrap{display:flex;align-items:center;justify-content:center;min-height:100vh;padding:24px;box-sizing:border-box;}
    .label{position:relative;width:${sizePx.w}px;height:${sizePx.h}px;background:#fff;overflow:hidden;font-family:Arial,sans-serif;box-shadow:0 10px 40px rgba(0,0,0,.18);}
    @media print{ html,body{background:#fff;} .wrap{padding:0;min-height:auto;} .label{box-shadow:none;} }
  </style></head><body><div class="wrap"><div class="label">${body}</div></div>${els.some(isAutoFit) ? `<script>${FIT_SCRIPT}</script>` : ''}</body></html>`;
}

/* ============================================================================
   Edytor RAPORTU produktów (PACKFLOW_REPORT_V1).
   Raport „Raport produktów" to wielostronicowy, paginowany dokument A4 (nagłówek
   + opcjonalne podsumowanie + tabela produktów łamana między stronami) — nie da się
   go odwzorować blokami pozycjonowanymi bezwzględnie, więc ma własną, wizualną
   powierzchnię edycji (knobki: tytuł / kolory / karty podsumowania / kolumny / stopka /
   wierszy-na-stronę) serializowaną do samowystarczalnego Liquid.
   Trzymaj eksport zgodny z serwerowym `reportModel.ts` (api) — szablon zapisany w
   przeglądarce musi renderować się identycznie po stronie serwera.
   ========================================================================== */
const REPORT_MARKER = 'PACKFLOW_REPORT_V1';

type ReportSummaryKey = 'ordersCount' | 'unfulfilledCount' | 'partialCount' | 'totalItems';
type ReportModel = {
  title: string;
  subtitle: string;
  accent: string;
  showLogo: boolean;
  showSummary: boolean;
  summaryCards: Array<{ key: ReportSummaryKey; label: string }>;
  // `variant` przełącza też grupowanie w UCS: włączona — wiersz na wariant, wyłączona — warianty
  // produktu zsumowane w jednym wierszu (serwer: reportGroupBy w reportModel.ts).
  columns: { lp: boolean; sku: boolean; name: boolean; variant: boolean; quantity: boolean };
  headers: { lp: string; sku: string; name: string; variant: string; quantity: string };
  zebra: boolean;
  footer: string;
  rowsPerPage: number;
};

const DEFAULT_REPORT_MODEL: ReportModel = {
  title: 'Raport produktów do spakowania',
  subtitle: '',
  accent: '#333333',
  showLogo: true,
  showSummary: true,
  summaryCards: [
    { key: 'ordersCount', label: 'Zamówienia' },
    { key: 'unfulfilledCount', label: 'Niezrealizowane' },
    { key: 'partialCount', label: 'Częściowe' },
    { key: 'totalItems', label: 'Sztuk ogółem' },
  ],
  // Wariant domyślnie wyłączony: szablon zapisany przed dodaniem kolumny dalej sumuje warianty.
  columns: { lp: true, sku: true, name: true, variant: false, quantity: true },
  headers: { lp: 'Lp.', sku: 'SKU', name: 'Nazwa produktu', variant: 'Wariant', quantity: 'Ilość' },
  zebra: true,
  footer: 'Raport zbiorczy — strona {{ page }} z {{ totalPages }}',
  rowsPerPage: 12,
};

// Scalanie z domyślnymi klucz po kluczu także w `columns`/`headers` — szablon sprzed kolumny
// nie ma jej klucza i ma dostać wartość domyślną (kolumna wyłączona), a nie `undefined`.
function withReportDefaults(model: Partial<ReportModel>): ReportModel {
  return {
    ...DEFAULT_REPORT_MODEL,
    ...model,
    columns: { ...DEFAULT_REPORT_MODEL.columns, ...model.columns },
    headers: { ...DEFAULT_REPORT_MODEL.headers, ...model.headers },
  };
}

const SUMMARY_VALUE: Record<ReportSummaryKey, string> = {
  ordersCount: '{{ ordersCount }}',
  unfulfilledCount: '{{ unfulfilledCount }}',
  partialCount: '{{ partialCount }}',
  totalItems: '{{ totalItems }}',
};
const SUMMARY_KEY_OPTIONS: Array<{ key: ReportSummaryKey; label: string }> = [
  { key: 'ordersCount', label: 'Zamówienia' },
  { key: 'unfulfilledCount', label: 'Niezrealizowane' },
  { key: 'partialCount', label: 'Częściowe' },
  { key: 'totalItems', label: 'Sztuk ogółem' },
];

function clampRows(n: number): number {
  return Number.isFinite(n) && n >= 1 && n <= 100 ? Math.floor(n) : DEFAULT_REPORT_MODEL.rowsPerPage;
}
function footerToLiquid(footer: string): string {
  return esc(footer).replace(/\{\{\s*page\s*\}\}/g, '{{ forloop.index }}');
}

// CSS współdzielone przez eksport (Liquid) i podgląd, by uniknąć rozjazdu wyglądu.
function reportCssBlock(m: ReportModel): string {
  const zebra = m.zebra ? '\n        tr:nth-child(even) { background-color: #f9f9f9; }' : '';
  return `
        @page { size: A4; margin: 15mm; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #333; font-size: 12pt; }
        .page { page-break-after: always; position: relative; min-height: 100vh; }
        .page:last-child { page-break-after: auto; }
        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid ${m.accent}; padding-bottom: 15px; }
        .logo { max-width: 351px; max-height: 117px; margin-bottom: 10px; }
        h1 { margin: 10px 0 5px 0; font-size: 20px; color: #333; }
        .subtitle { font-size: 11px; color: #666; margin: 5px 0; }
        .info { font-size: 12px; color: #666; margin: 5px 0; }
        .summary { display: flex; justify-content: space-around; background-color: #f5f5f5; padding: 12px; border-radius: 5px; margin-bottom: 15px; }
        .summary-item { text-align: center; }
        .summary-label { font-size: 11px; color: #666; }
        .summary-value { font-size: 18px; font-weight: bold; color: #333; margin-top: 3px; }
        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        th { background-color: ${m.accent}; color: white; padding: 10px; text-align: left; font-weight: bold; font-size: 12px; }
        td { padding: 8px 10px; border-bottom: 1px solid #ddd; font-size: 11px; }${zebra}
        .sku { font-family: monospace; font-weight: bold; color: #333; }
        .quantity { font-weight: bold; font-size: 14px; text-align: center; color: #333; }
        .footer { position: absolute; bottom: 0; left: 0; right: 0; text-align: center; font-size: 10px; color: #999; padding-bottom: 10mm; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } .page { min-height: auto; } }`;
}

function reportHeaderCells(m: ReportModel): string {
  const cells: string[] = [];
  if (m.columns.lp) cells.push(`<th style="width: 10%;">${esc(m.headers.lp)}</th>`);
  if (m.columns.sku) cells.push(`<th style="width: 15%;">${esc(m.headers.sku)}</th>`);
  // Kolumna Wariant zabiera szerokość kolumnie nazwy — wiersz dalej sumuje się do 100%.
  if (m.columns.name) cells.push(`<th style="width: ${m.columns.variant ? 35 : 55}%;">${esc(m.headers.name)}</th>`);
  if (m.columns.variant) cells.push(`<th style="width: 20%;">${esc(m.headers.variant)}</th>`);
  if (m.columns.quantity) cells.push(`<th style="width: 20%; text-align: center;">${esc(m.headers.quantity)}</th>`);
  return cells.join('');
}

function exportReportLiquid(model: ReportModel): string {
  const m = withReportDefaults(model);
  const rows = clampRows(m.rowsPerPage);
  const marker = `<!--${REPORT_MARKER} ${JSON.stringify(m)}-->`;
  const bodyCells: string[] = [];
  if (m.columns.lp) bodyCells.push(`<td>{{ forloop.parentloop.index0 | times: ${rows} | plus: forloop.index }}</td>`);
  if (m.columns.sku) bodyCells.push('<td class="sku">{{ product.sku }}</td>');
  if (m.columns.name) bodyCells.push('<td>{{ product.name }}</td>');
  if (m.columns.variant) bodyCells.push('<td>{{ product.variant }}</td>');
  if (m.columns.quantity) bodyCells.push('<td class="quantity">{{ product.quantity }}</td>');

  const summary = m.showSummary && m.summaryCards.length
    ? `        {% if forloop.first %}
        <div class="summary">
${m.summaryCards.map((c) => `            <div class="summary-item"><div class="summary-label">${esc(c.label)}</div><div class="summary-value">${SUMMARY_VALUE[c.key]}</div></div>`).join('\n')}
        </div>
        {% endif %}
`
    : '';
  const logo = m.showLogo
    ? `            {% if shop_logo_url %}<img src="{{ shop_logo_url }}" alt="Logo" class="logo">{% endif %}\n`
    : '';
  const subtitle = m.subtitle.trim() ? `            <div class="subtitle">${esc(m.subtitle)}</div>\n` : '';

  return `<!DOCTYPE html>
<html lang="pl">
<head>
    <meta charset="UTF-8">
    ${marker}
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${esc(m.title)}</title>
    <style>${reportCssBlock(m)}
    </style>
</head>
<body>
    {% for pageProducts in productPages %}
    <div class="page">
        <div class="header">
${logo}            <h1>${esc(m.title)}</h1>
${subtitle}            <div class="info">Data wygenerowania: {{ date }}</div>
            {% if dateRange %}<div class="info" style="font-weight: bold; color: #0066cc;">{{ dateRange }}</div>{% endif %}
            {% if preview %}<div class="info" style="font-weight: bold; color: #ff9800;">WYDRUK POGLĄDOWY</div>{% endif %}
        </div>
${summary}        <table>
            <thead><tr>${reportHeaderCells(m)}</tr></thead>
            <tbody>
                {% for product in pageProducts %}<tr>${bodyCells.join('')}</tr>{% endfor %}
            </tbody>
        </table>
        <div class="footer"><p>${footerToLiquid(m.footer)}</p></div>
    </div>
    {% endfor %}
</body>
</html>
`;
}

function importReportModel(content: string): ReportModel | null {
  const match = content.match(new RegExp(`<!--${REPORT_MARKER} (.*?)-->`, 's'));
  if (!match || !match[1]) return null;
  try {
    return withReportDefaults(JSON.parse(match[1]) as Partial<ReportModel>);
  } catch {
    return null;
  }
}

// Podgląd WYSIWYG: jedna strona z danymi przykładowymi (bez Liquid), te same style co eksport.
// Dane w obu grupowaniach UCS, żeby podgląd pokazywał, co naprawdę zmienia kolumna Wariant
// (zgodnie z serwerowym podglądem PDF w routes.templates.ts).
const REPORT_SAMPLE_BY_VARIANT = [
  { sku: 'BUL-WAW-SEZ', name: 'Bułka Wawelska', variant: 'Sezam biały', quantity: 20 },
  { sku: 'BUL-WAW-SLO', name: 'Bułka Wawelska', variant: 'Słonecznik', quantity: 12 },
  { sku: 'CHL-ZYT', name: 'Chleb żytni na zakwasie 700g', variant: '', quantity: 18 },
  { sku: 'CRO-MAS', name: 'Croissant maślany', variant: '', quantity: 12 },
  { sku: '', name: 'Papier pakowy (dodatek)', variant: '', quantity: 6 },
];
const REPORT_SAMPLE_BY_PRODUCT = [
  { sku: 'BUL-WAW-SEZ, BUL-WAW-SLO', name: 'Bułka Wawelska', variant: '', quantity: 32 },
  ...REPORT_SAMPLE_BY_VARIANT.slice(2),
];
function renderReportPreviewHtml(model: ReportModel): string {
  const m = withReportDefaults(model);
  const sample = m.columns.variant ? REPORT_SAMPLE_BY_VARIANT : REPORT_SAMPLE_BY_PRODUCT;
  const totalItems = sample.reduce((s, p) => s + p.quantity, 0);
  const sampleVals: Record<ReportSummaryKey, number> = { ordersCount: 14, unfulfilledCount: 12, partialCount: 2, totalItems };
  const logo = m.showLogo
    ? '<div class="logo" style="display:inline-flex;align-items:center;justify-content:center;width:240px;height:70px;background:#eef1f5;border:1px dashed #9aa6b2;color:#7a8794;font-weight:700;letter-spacing:1px;">LOGO</div>'
    : '';
  const subtitle = m.subtitle.trim() ? `<div class="subtitle">${esc(m.subtitle)}</div>` : '';
  const summary = m.showSummary && m.summaryCards.length
    ? `<div class="summary">${m.summaryCards.map((c) => `<div class="summary-item"><div class="summary-label">${esc(c.label)}</div><div class="summary-value">${sampleVals[c.key]}</div></div>`).join('')}</div>`
    : '';
  const rows = sample.map((p, i) => {
    const tds: string[] = [];
    if (m.columns.lp) tds.push(`<td>${i + 1}</td>`);
    if (m.columns.sku) tds.push(`<td class="sku">${esc(p.sku)}</td>`);
    if (m.columns.name) tds.push(`<td>${esc(p.name)}</td>`);
    if (m.columns.variant) tds.push(`<td>${esc(p.variant)}</td>`);
    if (m.columns.quantity) tds.push(`<td class="quantity">${p.quantity}</td>`);
    return `<tr>${tds.join('')}</tr>`;
  }).join('');
  const footer = esc(m.footer).replace(/\{\{\s*page\s*\}\}/g, '1').replace(/\{\{\s*totalPages\s*\}\}/g, '1');
  return `<!DOCTYPE html><html lang="pl"><head><meta charset="UTF-8"><style>
    html,body{margin:0;padding:0;background:#e9edf3;}
    .wrap{padding:24px;box-sizing:border-box;}
    .sheet{background:#fff;max-width:794px;margin:0 auto;padding:40px;box-shadow:0 10px 40px rgba(0,0,0,.18);min-height:1000px;position:relative;}
    ${reportCssBlock(m)}
  </style></head><body><div class="wrap"><div class="sheet">
    <div class="header">${logo}<h1>${esc(m.title)}</h1>${subtitle}<div class="info">Data wygenerowania: 24.06.2026, 08:00</div><div class="info" style="font-weight:bold;color:#ff9800;">WYDRUK POGLĄDOWY</div></div>
    ${summary}
    <table><thead><tr>${reportHeaderCells(m)}</tr></thead><tbody>${rows}</tbody></table>
    <div class="footer" style="position:static;margin-top:30px;"><p>${footer}</p></div>
  </div></div></body></html>`;
}

/* Wizualny edytor raportu produktów — knobki po lewej, podgląd A4 na żywo po prawej. */
function ReportEditor({ model, onChange }: { model: ReportModel; onChange: (m: ReportModel) => void }) {
  const set = (patch: Partial<ReportModel>) => onChange({ ...model, ...patch });
  const setCol = (k: keyof ReportModel['columns'], v: boolean) => set({ columns: { ...model.columns, [k]: v } });
  const setHdr = (k: keyof ReportModel['headers'], v: string) => set({ headers: { ...model.headers, [k]: v } });
  const setCard = (i: number, patch: Partial<{ key: ReportSummaryKey; label: string }>) =>
    set({ summaryCards: model.summaryCards.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const removeCard = (i: number) => set({ summaryCards: model.summaryCards.filter((_, idx) => idx !== i) });
  const addCard = () => set({ summaryCards: [...model.summaryCards, { key: 'totalItems', label: 'Nowa karta' }] });

  const colDefs: Array<[keyof ReportModel['columns'], keyof ReportModel['headers'], string]> = [
    ['lp', 'lp', 'Lp.'], ['sku', 'sku', 'SKU'], ['name', 'name', 'Nazwa'], ['variant', 'variant', 'Wariant'],
    ['quantity', 'quantity', 'Ilość'],
  ];

  return (
    <div className="le-grid" style={{ gridTemplateColumns: '340px 1fr' }}>
      <div className="le-palette" style={{ overflowY: 'auto', maxHeight: '78vh' }}>
        <div className="sb-sec" style={{ padding: '0 2px 8px' }}>Treść</div>
        <div className="le-field"><label>Tytuł</label><input value={model.title} onChange={(e) => set({ title: e.target.value })} /></div>
        <div className="le-field"><label>Podtytuł (puste = ukryty)</label><input value={model.subtitle} placeholder="np. Magazyn Kraków" onChange={(e) => set({ subtitle: e.target.value })} /></div>

        <div className="sb-sec" style={{ padding: '14px 2px 8px' }}>Wygląd</div>
        <div className="le-field"><label>Kolor wiodący (nagłówek + tabela)</label><input type="color" value={model.accent} onChange={(e) => set({ accent: e.target.value })} style={{ width: 48, height: 32, padding: 2 }} /></div>
        <div className="flex" style={{ gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          <span className="set-row" style={{ border: 0, padding: 0, gap: 6 }}><span style={{ fontSize: 13 }}>Logo</span><Toggle on={model.showLogo} onChange={(v) => set({ showLogo: v })} /></span>
          <span className="set-row" style={{ border: 0, padding: 0, gap: 6 }}><span style={{ fontSize: 13 }}>Pasy w wierszach</span><Toggle on={model.zebra} onChange={(v) => set({ zebra: v })} /></span>
        </div>
        <div className="le-field"><label>Wierszy na stronę: {model.rowsPerPage}</label><input type="range" min={5} max={40} value={model.rowsPerPage} onChange={(e) => set({ rowsPerPage: clampRows(+e.target.value) })} /></div>

        <div className="sb-sec" style={{ padding: '14px 2px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
          Podsumowanie <Toggle on={model.showSummary} onChange={(v) => set({ showSummary: v })} />
        </div>
        {model.showSummary && (
          <>
            {model.summaryCards.map((c, i) => (
              <div key={i} className="le-field" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={{ flex: 1 }} value={c.label} onChange={(e) => setCard(i, { label: e.target.value })} />
                <select value={c.key} onChange={(e) => setCard(i, { key: e.target.value as ReportSummaryKey })} style={{ width: 120 }}>
                  {SUMMARY_KEY_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <button className="btn btn-icon btn-ghost" title="Usuń kartę" onClick={() => removeCard(i)}><Icon name="x" size={14} /></button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={addCard}><Icon name="plus" size={14} /> Dodaj kartę</button>
          </>
        )}

        <div className="sb-sec" style={{ padding: '14px 2px 8px' }}>Kolumny tabeli</div>
        {colDefs.map(([col, hdr, fallback]) => (
          <div key={col} className="le-field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Toggle on={model.columns[col]} onChange={(v) => setCol(col, v)} />
            <input style={{ flex: 1 }} value={model.headers[hdr]} placeholder={fallback} disabled={!model.columns[col]} onChange={(e) => setHdr(hdr, e.target.value)} />
          </div>
        ))}
        <div className="muted" style={{ fontSize: 12, marginTop: -6 }}>
          {model.columns.variant
            ? 'Wariant włączony: każdy wariant produktu to osobny wiersz.'
            : 'Wariant wyłączony: warianty produktu są sumowane w jednym wierszu.'}
        </div>

        <div className="sb-sec" style={{ padding: '14px 2px 8px' }}>Stopka</div>
        <div className="le-field"><label>Tekst stopki (zmienne: {'{{ page }}'} , {'{{ totalPages }}'})</label><input value={model.footer} onChange={(e) => set({ footer: e.target.value })} /></div>
      </div>

      <div className="le-canvas" style={{ padding: 0, background: '#e9edf3' }}>
        <iframe title="report-preview" style={{ width: '100%', height: '78vh', border: 0 }} srcDoc={renderReportPreviewHtml(model)} />
      </div>
    </div>
  );
}

type TemplateMeta = { id: string; name: string; description?: string; format?: string; category?: string; builtin?: boolean };
type NameModal = { mode: 'create' | 'rename'; value: string };

export default function LabelEditorPage() {
  const push = useAdminToast();
  const [templates, setTemplates] = useState<TemplateMeta[]>([]);
  const [tpl, setTpl] = useState('');
  const [els, setEls] = useState<El[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [rawMode, setRawMode] = useState(false);
  const [raw, setRaw] = useState('');
  // != null gdy edytowany szablon to RAPORT (PACKFLOW_REPORT_V1) — wtedy zamiast kanwy
  // bloków pokazujemy wizualny edytor raportu (ReportEditor).
  const [reportModel, setReportModel] = useState<ReportModel | null>(null);
  const [pageMm, setPageMm] = useState<Mm>(DEFAULT_MM);
  const [pageOpts, setPageOpts] = useState<PageOpts>({});
  const [sizePx, setSizePx] = useState<{ w: number; h: number }>({ w: mmToPx(DEFAULT_MM.w), h: mmToPx(DEFAULT_MM.h) });
  const [zoom, setZoom] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [snap, setSnap] = useState(true);
  const [nameModal, setNameModal] = useState<NameModal | null>(null);
  const drag = useRef<{ id: number; mode: 'move' | 'resize'; dx: number; dy: number; sw: number; sh: number; sx: number; sy: number } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  /* lista szablonów (wbudowane + własne) */
  const loadTemplates = React.useCallback((selectId?: string) => {
    return axios.get(`${API_URL}/api/templates`)
      .then((r) => {
        const list: TemplateMeta[] = Array.isArray(r.data) ? r.data : [];
        setTemplates(list);
        setTpl((prev) => (selectId ? selectId : (prev || list[0]?.id || '')));
        return list;
      })
      .catch(() => { push('Nie udało się pobrać listy szablonów', 'err'); return [] as TemplateMeta[]; });
  }, [push]);

  // Pobranie listy raz przy montażu (świeże pobrania robią akcje CRUD przez loadTemplates).
  useEffect(() => { loadTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* wczytanie szablonu */
  useEffect(() => {
    if (!tpl) return;
    axios.get(`${API_URL}/api/templates/${tpl}`)
      .then((r) => {
        const content: string = r.data?.content || '';
        setRaw(content);
        // Raport produktów: rozpoznajemy po markerze PACKFLOW_REPORT_V1 lub kategorii „report"
        // z listy szablonów (legacy products_summary bez markera → domyślny model + migracja
        // przy zapisie). Otwiera się w wizualnym edytorze raportu zamiast kanwy bloków.
        const meta = templates.find((t) => t.id === tpl);
        const rm = importReportModel(content);
        if (rm || meta?.category === 'report' || tpl === 'products_summary') {
          setReportModel(rm || DEFAULT_REPORT_MODEL);
          setEls([]);
          setRawMode(false);
          setSel(null);
          return;
        }
        setReportModel(null);
        const parsed = importLiquid(content);
        // Etykiety produktowe zapisane starszym edytorem nie mają `repeat` — przywracamy je z układu
        // domyślnego, bo bez pętli „na sztukę" drukowała się jedna etykieta bez nazwy produktu.
        setPageOpts(parsed
          ? { repeat: parsed.repeat ?? BUILTIN_DEFAULTS[tpl]?.repeat, rotate: parsed.rotate }
          : { repeat: BUILTIN_DEFAULTS[tpl]?.repeat });
        if (parsed) {
          const mm = parsed.pageMm || TEMPLATE_DEFAULT_MM[tpl] || DEFAULT_MM;
          const px = { w: mmToPx(mm.w), h: mmToPx(mm.h) };
          let next = parsed.els;
          if (parsed.size && parsed.size.w && parsed.size.h) {
            const fx = px.w / parsed.size.w; const fy = px.h / parsed.size.h;
            if (Math.abs(fx - 1) > 0.01 || Math.abs(fy - 1) > 0.01) {
              next = next.map((el) => ({
                ...el,
                x: Math.round(el.x * fx), y: Math.round(el.y * fy),
                w: el.w != null ? Math.round(el.w * fx) : el.w,
                h: el.h != null ? Math.round(el.h * fy) : el.h,
                fontSize: el.fontSize ? Math.max(6, Math.round(el.fontSize * fy)) : el.fontSize,
              }));
            }
          }
          setEls(next); setPageMm(mm); setSizePx(px); setRawMode(false);
        } else if (BUILTIN_DEFAULTS[tpl]) {
          // Brak modelu V2 (świeży seed/legacy) → czysty układ domyślny w trybie wizualnym,
          // dzięki czemu wbudowane etykiety są drag & drop tak jak „Etykiety produktów”.
          const def = BUILTIN_DEFAULTS[tpl];
          setEls(cloneEls(def.els));
          setPageMm(def.pageMm);
          setSizePx({ w: mmToPx(def.pageMm.w), h: mmToPx(def.pageMm.h) });
          setRawMode(false);
        } else if (content.trim()) {
          // Własny szablon z surową treścią bez modelu V2 → edycja źródła.
          const mm = TEMPLATE_DEFAULT_MM[tpl] || DEFAULT_MM;
          setPageMm(mm); setSizePx({ w: mmToPx(mm.w), h: mmToPx(mm.h) });
          setEls([]); setRawMode(true);
        } else {
          // Nowy, pusty typ własny → czyste płótno wizualne.
          const mm = TEMPLATE_DEFAULT_MM[tpl] || DEFAULT_MM;
          setPageMm(mm); setSizePx({ w: mmToPx(mm.w), h: mmToPx(mm.h) });
          setEls([]); setRawMode(false);
        }
        setSel(null);
      })
      .catch(() => push('Nie udało się wczytać szablonu', 'err'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl]);

  /* drag + resize */
  useEffect(() => {
    function move(e: PointerEvent) {
      if (!drag.current) return;
      const paper = document.getElementById('le-paper')?.getBoundingClientRect();
      if (!paper) return;
      const d = drag.current;
      if (d.mode === 'move') {
        let x = (e.clientX - paper.left) / zoom - d.dx;
        let y = (e.clientY - paper.top) / zoom - d.dy;
        if (snap) { x = Math.round(x / 4) * 4; y = Math.round(y / 4) * 4; }
        x = Math.max(0, Math.min(x, sizePx.w - 8));
        y = Math.max(0, Math.min(y, sizePx.h - 8));
        setEls((arr) => arr.map((el) => (el.id === d.id ? { ...el, x: Math.round(x), y: Math.round(y) } : el)));
      } else {
        let w = d.sw + ((e.clientX - paper.left) / zoom - d.sx);
        let h = d.sh + ((e.clientY - paper.top) / zoom - d.sy);
        if (snap) { w = Math.round(w / 4) * 4; h = Math.round(h / 4) * 4; }
        w = Math.max(16, Math.round(w));
        h = Math.max(2, Math.round(h));
        setEls((arr) => arr.map((el) => (el.id === d.id ? { ...el, w, ...(el.h != null ? { h } : {}) } : el)));
      }
    }
    function up() { drag.current = null; }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [zoom, snap, sizePx.w, sizePx.h]);

  function onElPointerDown(e: React.PointerEvent, el: El) {
    setSel(el.id);
    const paper = document.getElementById('le-paper')?.getBoundingClientRect();
    if (!paper) return;
    drag.current = { id: el.id, mode: 'move', dx: (e.clientX - paper.left) / zoom - el.x, dy: (e.clientY - paper.top) / zoom - el.y, sw: el.w || 0, sh: el.h || 0, sx: 0, sy: 0 };
    e.preventDefault();
    e.stopPropagation();
  }
  function onResizeDown(e: React.PointerEvent, el: El) {
    setSel(el.id);
    const paper = document.getElementById('le-paper')?.getBoundingClientRect();
    if (!paper) return;
    drag.current = { id: el.id, mode: 'resize', dx: 0, dy: 0, sw: el.w || 0, sh: el.h || 0, sx: (e.clientX - paper.left) / zoom, sy: (e.clientY - paper.top) / zoom };
    e.preventDefault();
    e.stopPropagation();
  }

  /* operacje na blokach */
  function addBlock(type: BlockType) {
    const el = defaultEl(type, 20, 20);
    setEls((a) => [...a, el]);
    setSel(el.id);
  }
  function update(patch: Partial<El>) { setEls((a) => a.map((el) => (el.id === sel ? { ...el, ...patch } : el))); }
  function del() { setEls((a) => a.filter((el) => el.id !== sel)); setSel(null); }
  function duplicate() {
    const cur = els.find((e) => e.id === sel); if (!cur) return;
    const copy = { ...cur, id: ++UID, x: cur.x + 12, y: cur.y + 12 };
    setEls((a) => [...a, copy]); setSel(copy.id);
  }
  function bringToFront() { setEls((a) => { const cur = a.find((e) => e.id === sel); if (!cur) return a; return [...a.filter((e) => e.id !== sel), cur]; }); }

  function applyPageMm(next: Mm) {
    const px = { w: mmToPx(next.w), h: mmToPx(next.h) };
    const fx = sizePx.w ? px.w / sizePx.w : 1; const fy = sizePx.h ? px.h / sizePx.h : 1;
    setEls((arr) => arr.map((el) => ({
      ...el,
      x: Math.round(el.x * fx), y: Math.round(el.y * fy),
      w: el.w != null ? Math.round(el.w * fx) : el.w,
      h: el.h != null ? Math.round(el.h * fy) : el.h,
      fontSize: el.fontSize ? Math.max(6, Math.round(el.fontSize * fy)) : el.fontSize,
    })));
    setPageMm(next); setSizePx(px);
  }
  function onPickFormat(id: string) {
    const f = FORMATS.find((x) => x.id === id); if (f) applyPageMm({ w: f.w, h: f.h });
  }
  function onUploadImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { push('Obraz jest za duży (max 1.5 MB)', 'err'); return; }
    const reader = new FileReader();
    reader.onload = () => { if (sel != null) update({ src: String(reader.result) }); };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function save() {
    try {
      // Zabezpieczenie: pusty edytor wizualny BLOKÓW nie może po cichu nadpisać ręcznego kodu
      // HTML. Nie dotyczy raportu (ma zawsze pełny model) ani trybu kodu.
      if (!rawMode && !reportModel && els.length === 0 && raw.trim().length > 0) {
        const ok = window.confirm(
          'Edytor wizualny jest pusty i nadpisze istniejący kod HTML tego szablonu. Zapisać pusty układ?'
        );
        if (!ok) return;
      }
      const content = rawMode ? raw : reportModel ? exportReportLiquid(reportModel) : exportLiquid(els, sizePx, pageMm, pageOpts);
      await axios.post(`${API_URL}/api/templates/${tpl}`, { content });
      push('Szablon zapisany ✓');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu szablonu', 'err'); }
  }

  // Przełącznik trybu źródła/wizualnego z informacją, gdy szablon ma własny kod HTML,
  // którego nie da się odwzorować blokami (pusty edytor wizualny zastąpi go dopiero po zapisie).
  function toggleRawMode() {
    // Raport: do kodu serializujemy bieżący model; z kodu re-importujemy model (gdy marker jest).
    if (reportModel) {
      if (!rawMode) {
        setRaw(exportReportLiquid(reportModel));
        setRawMode(true);
      } else {
        const m = importReportModel(raw);
        if (m) setReportModel(m);
        setRawMode(false);
      }
      return;
    }
    const goingVisual = rawMode; // jesteśmy w kodzie → przełączamy na wizualny
    if (goingVisual && els.length === 0 && raw.trim().length > 0) {
      push('Ten szablon ma własny kod HTML — pusty edytor wizualny zastąpi go dopiero po „Zapisz”.', 'info');
    }
    setRawMode((v) => !v);
  }

  /* ---- przywróć wbudowany układ domyślny (naprawia „rozjechane" etykiety) ---- */
  function restoreDefault() {
    const def = BUILTIN_DEFAULTS[tpl];
    if (!def) return;
    setEls(cloneEls(def.els));
    setPageMm(def.pageMm);
    setSizePx({ w: mmToPx(def.pageMm.w), h: mmToPx(def.pageMm.h) });
    // Obrót zostaje (zależy od papieru, nie od bloków); pętla „na sztukę" wraca z układu domyślnego.
    setPageOpts((o) => ({ ...o, repeat: def.repeat }));
    setRawMode(false);
    setSel(null);
    push('Przywrócono układ domyślny — kliknij „Zapisz”, aby utrwalić');
  }

  /* ---- CRUD typów etykiet ---- */
  async function createTemplate(name: string) {
    try {
      const r = await axios.post(`${API_URL}/api/templates`, { name });
      await loadTemplates(r.data?.id);
      push('Utworzono nowy typ etykiety ✓');
    } catch (e: any) { push(e?.response?.data?.error || 'Nie udało się utworzyć szablonu', 'err'); }
  }
  async function renameTemplate(name: string) {
    try {
      await axios.patch(`${API_URL}/api/templates/${tpl}`, { name });
      await loadTemplates(tpl);
      push('Zmieniono nazwę ✓');
    } catch (e: any) { push(e?.response?.data?.error || 'Nie udało się zmienić nazwy', 'err'); }
  }
  async function deleteTemplate() {
    const meta = templates.find((t) => t.id === tpl);
    if (!meta || meta.builtin) return;
    if (!window.confirm(`Usunąć typ etykiety „${meta.name}”? Tej operacji nie można cofnąć.`)) return;
    try {
      await axios.delete(`${API_URL}/api/templates/${tpl}`);
      const remaining = templates.filter((t) => t.id !== tpl);
      await loadTemplates(remaining[0]?.id);
      push('Usunięto typ etykiety ✓');
    } catch (e: any) { push(e?.response?.data?.error || 'Nie udało się usunąć szablonu', 'err'); }
  }
  function submitNameModal() {
    if (!nameModal) return;
    const v = nameModal.value.trim();
    if (!v) { push('Podaj nazwę', 'err'); return; }
    const mode = nameModal.mode;
    setNameModal(null);
    if (mode === 'create') createTemplate(v); else renameTemplate(v);
  }

  const selEl = els.find((e) => e.id === sel);
  const selMeta = templates.find((t) => t.id === tpl);
  const isCustom = !!selMeta && !selMeta.builtin;
  const curFormat = useMemo(() => FORMATS.find((f) => f.w === pageMm.w && f.h === pageMm.h)?.id || 'custom', [pageMm]);

  return (
    <div className="page" style={{ maxWidth: 1560 }}>
      <div className="page-head">
        <div><h1>Edytor etykiet</h1><div className="ph-sub">Przeciągaj, skaluj, ustaw wymiar · podgląd na żywo</div></div>
        <div className="actions">
          <select className="select" value={tpl} onChange={(e) => setTpl(e.target.value)}>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn btn-icon btn-ghost" title="Nowy typ etykiety" onClick={() => setNameModal({ mode: 'create', value: '' })}>
            <Icon name="plus" size={16} />
          </button>
          {isCustom && (
            <>
              <button className="btn btn-icon btn-ghost" title="Zmień nazwę typu" onClick={() => setNameModal({ mode: 'rename', value: selMeta?.name || '' })}>
                <Icon name="pencil" size={16} />
              </button>
              <button className="btn btn-icon btn-ghost" title="Usuń typ etykiety" style={{ color: 'var(--danger-text)' }} onClick={deleteTemplate}>
                <Icon name="trash-2" size={16} />
              </button>
            </>
          )}
          <button className="btn btn-ghost" onClick={toggleRawMode}>
            <Icon name={rawMode ? 'layout-template' : 'code'} size={16} /> {rawMode ? 'Edytor wizualny' : 'Kod Liquid'}
          </button>
          <button className="btn btn-ghost" onClick={() => setShowPreview(true)}><Icon name="eye" size={16} /> Podgląd</button>
          <button className="btn btn-primary" onClick={save}><Icon name="save" size={16} /> Zapisz</button>
        </div>
      </div>

      {/* pasek wymiarów / zoom (tylko edytor bloków — raport jest stałe A4) */}
      {!rawMode && !reportModel && (
        <div className="card" style={{ marginBottom: 14, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
            <Icon name="ruler" size={16} />
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-2)' }}>Wymiar etykiety</span>
            <select className="select" style={{ width: 200 }} value={curFormat} onChange={(e) => e.target.value !== 'custom' && onPickFormat(e.target.value)}>
              {FORMATS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              <option value="custom">Własny…</option>
            </select>
          </div>
          <div className="flex" style={{ gap: 6, alignItems: 'center' }}>
            <input className="select" style={{ width: 72 }} type="number" min={20} max={400} value={pageMm.w}
              onChange={(e) => applyPageMm({ ...pageMm, w: Math.max(20, Number(e.target.value) || 20) })} />
            <span className="muted">×</span>
            <input className="select" style={{ width: 72 }} type="number" min={20} max={400} value={pageMm.h}
              onChange={(e) => applyPageMm({ ...pageMm, h: Math.max(20, Number(e.target.value) || 20) })} />
            <span className="muted" style={{ fontSize: 13 }}>mm</span>
            <button className="btn btn-ghost btn-sm" title="Obróć (zamień szer./wys.)" onClick={() => applyPageMm({ w: pageMm.h, h: pageMm.w })}><Icon name="rotate-cw" size={14} /></button>
          </div>
          {BUILTIN_DEFAULTS[tpl] && (
            <button className="btn btn-ghost btn-sm" title="Przywróć domyślny układ tej etykiety" onClick={restoreDefault}>
              <Icon name="rotate-ccw" size={14} /> Przywróć domyślny
            </button>
          )}
          <div className="flex" style={{ gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
            <span className="set-row" style={{ border: 0, padding: 0, gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>Siatka</span>
              <Toggle on={snap} onChange={setSnap} />
            </span>
            <Icon name="zoom-out" size={15} />
            <input type="range" min={0.5} max={2} step={0.1} value={zoom} onChange={(e) => setZoom(+e.target.value)} style={{ width: 120 }} />
            <Icon name="zoom-in" size={15} />
            <span className="mono" style={{ fontSize: 12, width: 40 }}>{Math.round(zoom * 100)}%</span>
          </div>
        </div>
      )}

      {rawMode ? (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div className="card-head">
            <h3>Kod Liquid</h3>
            <span className="ch-sub">Edycja źródła. Przełącz na „Edytor wizualny”, aby projektować przeciąganiem.</span>
          </div>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} spellCheck={false}
            style={{ width: '100%', minHeight: 480, border: 0, outline: 'none', resize: 'vertical', padding: 18, font: '13px var(--font-mono)', background: 'var(--surface)', color: 'var(--fg-1)' }} />
        </div>
      ) : reportModel ? (
        <ReportEditor model={reportModel} onChange={setReportModel} />
      ) : (
        <div className="le-grid">
          {/* paleta */}
          <div className="le-palette">
            <div className="sb-sec" style={{ padding: '0 2px 10px' }}>Bloki</div>
            <div className="le-blocks" style={{ display: 'grid', gap: 8 }}>
              {BLOCK_DEFS.map((b) => (
                <div key={b.type} className="le-block" onClick={() => addBlock(b.type)} title="Kliknij, by dodać">
                  <Icon name={b.icon} size={16} /> {b.label}
                </div>
              ))}
            </div>
            <div className="sb-sec" style={{ padding: '16px 2px 8px' }}>Pola danych</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {VAR_CHIPS.map((v) => (
                <span key={v} className="le-var" style={{ cursor: 'pointer' }} title="Dodaj pole danych"
                  onClick={() => { const el = { ...defaultEl('variable', 20, 20), variable: v }; setEls((a) => [...a, el]); setSel(el.id); }}>
                  {v}
                </span>
              ))}
            </div>
          </div>

          {/* kanwa */}
          <div className="le-canvas">
            <div style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}>
              <div id="le-paper" className="le-paper" style={{ width: sizePx.w, height: sizePx.h }} onPointerDown={() => setSel(null)}>
                {els.map((el) => (
                  <div key={el.id} className={`le-el ${el.id === sel ? 'sel' : ''}`}
                    style={{ left: el.x, top: el.y, whiteSpace: el.type === 'text' ? 'pre-line' : 'normal' }}
                    onPointerDown={(e) => onElPointerDown(e, el)}>
                    <ElementView el={el} />
                    {el.id === sel && (
                      <span className="handle" style={{ cursor: 'nwse-resize' }} onPointerDown={(e) => onResizeDown(e, el)} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* właściwości */}
          <div className="le-props">
            <div className="sb-sec" style={{ padding: '0 2px 10px' }}>Właściwości</div>
            {!selEl && <div className="muted" style={{ fontSize: 13 }}>Kliknij blok na etykiecie, aby zmienić jego ustawienia. Dodawaj bloki z lewej palety.</div>}
            {selEl && (
              <div>
                <div className="le-field"><label>Typ</label><input value={BLOCK_DEFS.find((b) => b.type === selEl.type)?.label || selEl.type} disabled /></div>

                {selEl.type === 'text' && (
                  <div className="le-field"><label>Treść (możesz użyć {'{{ pola }}'})</label><textarea rows={3} value={selEl.text} onChange={(e) => update({ text: e.target.value })} /></div>
                )}
                {selEl.type === 'image' && (
                  <>
                    <div className="le-field"><label>Obraz</label>
                      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onUploadImage} />
                      <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => fileRef.current?.click()}><Icon name="upload" size={14} /> Wgraj z dysku</button>
                    </div>
                    <div className="le-field"><label>…lub URL obrazu</label><input value={selEl.src && selEl.src.startsWith('http') ? selEl.src : ''} placeholder="https://…/logo.png" onChange={(e) => update({ src: e.target.value })} /></div>
                  </>
                )}
                {(selEl.type === 'variable' || selEl.type === 'barcode' || selEl.type === 'qr') && (
                  <div className="le-field"><label>Pole danych</label>
                    <select value={selEl.variable} onChange={(e) => update({ variable: e.target.value })}>
                      {Object.keys(SAMPLE).map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                )}

                {(selEl.type === 'text' || selEl.type === 'variable') && (
                  <>
                    <div className="le-field"><label>{selEl.autoFit ? 'Maks. rozmiar czcionki' : 'Rozmiar czcionki'}: {selEl.fontSize}px</label><input type="range" min={6} max={42} value={selEl.fontSize} onChange={(e) => update({ fontSize: +e.target.value })} /></div>
                    <div className="le-field"><label>Wyrównanie</label>
                      <div className="flex" style={{ gap: 6 }}>
                        {(['left', 'center', 'right'] as const).map((a) => (
                          <button key={a} className={`btn btn-sm ${selEl.align === a ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1, justifyContent: 'center' }} onClick={() => update({ align: a })}>
                            <Icon name={`align-${a}`} size={14} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex" style={{ gap: 16, marginBottom: 12 }}>
                      <span className="set-row" style={{ border: 0, padding: 0, gap: 6 }}><span style={{ fontSize: 13 }}>Pogrub.</span><Toggle on={!!selEl.bold} onChange={(v) => update({ bold: v })} /></span>
                      <span className="set-row" style={{ border: 0, padding: 0, gap: 6 }}><span style={{ fontSize: 13 }}>Kursywa</span><Toggle on={!!selEl.italic} onChange={(v) => update({ italic: v })} /></span>
                    </div>
                    <div className="le-field"><label>Kolor tekstu</label><input type="color" value={selEl.color || '#111111'} onChange={(e) => update({ color: e.target.value })} style={{ width: 48, height: 32, padding: 2 }} /></div>
                    <span className="set-row" style={{ border: 0, padding: 0, gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 13 }}>Dopasuj rozmiar do pola</span>
                      <Toggle on={!!selEl.autoFit} onChange={(v) => update(v ? { autoFit: true, ...defaultFitField(selEl, els) } : { autoFit: false, h: undefined })} />
                    </span>
                    {selEl.autoFit && (
                      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
                        Czcionka zmniejsza się, gdy treść nie mieści się w polu (przerywana ramka) — suwak wyżej to rozmiar maksymalny. Pole zmienisz uchwytem albo suwakami.
                      </div>
                    )}
                  </>
                )}

                {(selEl.type === 'box') && (
                  <>
                    <div className="le-field"><label>Tło</label><input type="color" value={selEl.bg && selEl.bg !== 'transparent' ? selEl.bg : '#ffffff'} onChange={(e) => update({ bg: e.target.value })} style={{ width: 48, height: 32, padding: 2 }} /></div>
                    <div className="le-field"><label>Ramka</label><input type="color" value={selEl.borderColor || '#111111'} onChange={(e) => update({ borderColor: e.target.value })} style={{ width: 48, height: 32, padding: 2 }} /></div>
                    <div className="le-field"><label>Zaokrąglenie: {selEl.radius || 0}px</label><input type="range" min={0} max={40} value={selEl.radius || 0} onChange={(e) => update({ radius: +e.target.value })} /></div>
                    <div className="le-field"><label>Nagłówek ramki</label><input value={selEl.label || ''} placeholder="np. Uwagi" onChange={(e) => update({ label: e.target.value })} /></div>
                    <span className="set-row" style={{ border: 0, padding: 0, gap: 6, marginBottom: 12 }}>
                      <span style={{ fontSize: 13 }}>Przerywana ramka</span>
                      <Toggle on={!!selEl.dashed} onChange={(v) => update({ dashed: v })} />
                    </span>
                  </>
                )}
                {(selEl.type === 'line') && (
                  <div className="le-field"><label>Kolor linii</label><input type="color" value={selEl.bg || '#111111'} onChange={(e) => update({ bg: e.target.value })} style={{ width: 48, height: 32, padding: 2 }} /></div>
                )}
                {(selEl.type === 'table') && (
                  <div className="le-field"><label>Rozmiar czcionki: {selEl.fontSize}px</label><input type="range" min={7} max={18} value={selEl.fontSize || 11} onChange={(e) => update({ fontSize: +e.target.value })} /></div>
                )}

                {selEl.w != null && (
                  <div className="le-field"><label>Szerokość: {selEl.w}px</label><input type="range" min={16} max={sizePx.w} value={selEl.w} onChange={(e) => update({ w: +e.target.value })} /></div>
                )}
                {selEl.h != null && ((selEl.type !== 'text' && selEl.type !== 'variable') || selEl.autoFit) && (
                  <div className="le-field"><label>{selEl.autoFit ? 'Wysokość pola' : 'Wysokość'}: {selEl.h}px</label><input type="range" min={2} max={sizePx.h} value={selEl.h} onChange={(e) => update({ h: +e.target.value })} /></div>
                )}
                <div className="le-field"><label>Pozycja</label>
                  <div className="flex" style={{ gap: 6 }}>
                    <input style={{ width: '50%' }} type="number" value={selEl.x} onChange={(e) => update({ x: +e.target.value })} />
                    <input style={{ width: '50%' }} type="number" value={selEl.y} onChange={(e) => update({ y: +e.target.value })} />
                  </div>
                </div>

                <div className="flex" style={{ gap: 6, marginTop: 4 }}>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={duplicate}><Icon name="copy" size={14} /> Kopiuj</button>
                  <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={bringToFront}><Icon name="bring-to-front" size={14} /> Na wierzch</button>
                </div>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-text)', width: '100%', justifyContent: 'center', marginTop: 6 }} onClick={del}><Icon name="trash-2" size={14} /> Usuń element</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showPreview && (
        <div className="wiz-scrim" onClick={() => setShowPreview(false)}>
          <div className="wiz" style={{ width: 'min(760px, 94vw)', maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="wiz-head">
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}><Icon name="eye" size={18} /></div>
              <div className="wt">{reportModel ? 'Podgląd raportu — A4' : `Podgląd etykiety — ${pageMm.w} × ${pageMm.h} mm`}</div>
              <button className="btn btn-icon btn-ghost right" onClick={() => setShowPreview(false)}><Icon name="x" size={18} /></button>
            </div>
            <div className="wiz-body" style={{ padding: 0, background: '#e9edf3' }}>
              <iframe title="preview" style={{ width: '100%', height: '60vh', border: 0 }} srcDoc={reportModel ? renderReportPreviewHtml(reportModel) : previewDoc(els, sizePx, pageMm)} />
            </div>
            <div className="wiz-foot">
              <div className="muted" style={{ fontSize: 12, marginRight: 'auto' }}>Podgląd renderowany lokalnie na danych przykładowych.</div>
              <button className="btn btn-ghost" onClick={() => setShowPreview(false)}>Zamknij</button>
              <button className="btn btn-primary" onClick={() => {
                const ifr = document.querySelector('iframe[title="preview"]') as HTMLIFrameElement | null;
                ifr?.contentWindow?.print();
              }}><Icon name="printer" size={16} /> Drukuj / PDF</button>
            </div>
          </div>
        </div>
      )}

      {nameModal && (
        <div className="wiz-scrim" onClick={() => setNameModal(null)}>
          <div className="wiz" style={{ width: 'min(440px, 94vw)' }} onClick={(e) => e.stopPropagation()}>
            <div className="wiz-head">
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}>
                <Icon name={nameModal.mode === 'create' ? 'plus' : 'pencil'} size={18} />
              </div>
              <div className="wt">{nameModal.mode === 'create' ? 'Nowy typ etykiety' : 'Zmień nazwę typu'}</div>
              <button className="btn btn-icon btn-ghost right" onClick={() => setNameModal(null)}><Icon name="x" size={18} /></button>
            </div>
            <div className="wiz-body">
              <div className="le-field">
                <label>Nazwa</label>
                <input autoFocus value={nameModal.value} placeholder="np. Etykieta zwrotna"
                  onChange={(e) => setNameModal((m) => (m ? { ...m, value: e.target.value } : m))}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitNameModal(); }} />
              </div>
              {nameModal.mode === 'create' && (
                <div className="muted" style={{ fontSize: 12 }}>Po utworzeniu zaprojektuj etykietę w edytorze i zapisz.</div>
              )}
            </div>
            <div className="wiz-foot">
              <button className="btn btn-ghost" onClick={() => setNameModal(null)}>Anuluj</button>
              <button className="btn btn-primary" onClick={submitNameModal}>
                {nameModal.mode === 'create' ? 'Utwórz' : 'Zapisz nazwę'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
